import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ---- Token storage helpers (Point 71 — keep users signed in via refresh) ----
export function setTokens(access, refresh) {
  if (access) localStorage.setItem("cbc_token", access);
  if (refresh) localStorage.setItem("cbc_refresh", refresh);
}
export function clearTokens() {
  localStorage.removeItem("cbc_token");
  localStorage.removeItem("cbc_refresh");
}

let refreshInFlight = null;

// Single-flight refresh: many simultaneous 401s share ONE /auth/refresh call.
function doRefresh() {
  if (!refreshInFlight) {
    const rt = localStorage.getItem("cbc_refresh");
    refreshInFlight = axios
      .post(`${API}/auth/refresh`, rt ? { refresh_token: rt } : {}, { withCredentials: true })
      .then(({ data }) => {
        if (data?.access_token) localStorage.setItem("cbc_token", data.access_token);
        return data?.access_token || null;
      })
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

function redirectToLogin() {
  const p = window.location.pathname;
  if (p !== "/login") window.location.assign("/login");
}

// Attach token from localStorage as fallback (for cross-origin / cookie issues)
client.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem("cbc_token");
  if (tok) cfg.headers["Authorization"] = `Bearer ${tok}`;
  return cfg;
});

// On an expired access token, refresh once and retry the original request.
client.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const isRefreshCall = original?.url?.includes("/auth/refresh");
    if (!original || status !== 401 || detail !== "Token expired" || original._authRetry || isRefreshCall) {
      return Promise.reject(error);
    }
    original._authRetry = true; // exactly one retry per request — no loops
    try {
      const newToken = await doRefresh();
      original.headers = original.headers || {};
      if (newToken) original.headers["Authorization"] = `Bearer ${newToken}`;
      return client(original);
    } catch (refreshErr) {
      clearTokens();
      redirectToLogin();
      return Promise.reject(refreshErr);
    }
  }
);

export default client;

export function formatError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" • ");
  if (typeof detail?.msg === "string") return detail.msg;
  return String(detail);
}
